import { useMemo, useState } from "react";
import { KpiCard } from "@/components/kit";
import { LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer } from "recharts";
import { filterData, kpisOf, dailySeries, mean, fmtN, fmtPct, fmtInt, covLabel, C, SITE_COLORS } from "./icUtils";

const card = "mk-card p-5";

export const Spark = ({ pts, color = C.cyan }) => {
  if (!pts || pts.length < 2) return <div className="h-[26px]" />;
  const vs = pts.map((p) => p.v);
  const min = Math.min(...vs), max = Math.max(...vs);
  const norm = (v) => (max === min ? 13 : 24 - ((v - min) / (max - min)) * 22);
  const step = 100 / (pts.length - 1);
  return (
    <svg viewBox="0 0 100 26" className="h-[26px] w-full" preserveAspectRatio="none">
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={pts.map((p, i) => `${i * step},${norm(p.v)}`).join(" ")} />
    </svg>
  );
};

const TargetChip = ({ v, target, lower }) => {
  if (v == null || target == null) return null;
  const met = lower ? v <= target : v >= target;
  return (
    <span className={`rounded-full px-2 py-0.5 font-mono-hud text-[9px] uppercase tracking-[0.1em] ${met ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#F43F5E]/15 text-[#F43F5E]"}`}>
      {met ? "met" : "miss"} · tgt {target}{lower ? "" : "%"}
    </span>
  );
};

const SEGMENTS = ["Onshore", "Offshore", "Nesting"];
const SLA_TREND_PREF = ["VOC WhatsApp", "VOC Live Chat", "PMT Overall", "QSL", "Shrinkage", "AHT WhatsApp (sec) On Closure", "AHT Live Chat (sec)", "FRT WA", "FRT LC", "Volume Weekly WhatsApp", "Volume Weekly Live Chat"];
const wkShort = (label) => { const m = label.match(/Week\s*(\d+)/i); return m ? `W${m[1]}` : label; };

export default function ExecView({ data, slices, kpis, range }) {
  const [segment, setSegment] = useState("Onshore");
  const cov = data.coverage;

  const tiles = useMemo(() => {
    const s = slices;
    const vocSeries = dailySeries(s.voc, (rs) => { const n = rs.reduce((a, r) => a + r.n, 0); return n ? (100 * rs.reduce((a, r) => a + r.t2b, 0)) / n : null; });
    const mk = (rows, fn) => dailySeries(rows, fn);
    return [
      { k: "voc", label: "VOC Top-2-Box", v: kpis.voc, fmt: fmtPct, target: data.targets.voc, pts: vocSeries, color: C.green, cov: cov.voc },
      { k: "surveys", label: "Surveys", v: kpis.surveys, fmt: fmtInt, pts: mk(s.voc, (rs) => rs.reduce((a, r) => a + r.n, 0)), color: C.blue, cov: cov.voc },
      { k: "pmt", label: "PMT Score", v: kpis.pmt, fmt: fmtPct, target: data.targets.pmt, pts: mk(s.pmt, (rs) => mean(rs.map((r) => r.score).filter((v) => v != null))), color: C.purple, cov: cov.pmt },
      { k: "crit", label: "PMT Criticals", v: kpis.crit, fmt: fmtInt, lower: true, pts: mk(s.pmt, (rs) => rs.filter((r) => r.rating === "Critical").length), color: C.red, cov: cov.pmt },
      { k: "qsl", label: "QSL Score", v: kpis.qsl, fmt: fmtPct, target: data.targets.qsl, pts: mk(s.qsl, (rs) => mean(rs.map((r) => r.score).filter((v) => v != null))), color: C.yellow, cov: cov.qsl },
      { k: "ahtWA", label: "AHT WhatsApp", v: kpis.ahtWA, fmt: (v) => `${fmtN(v, 0)}s`, pts: mk(s.chat.filter((r) => r.ch === "WA"), (rs) => mean(rs.map((r) => r.aht).filter((v) => v != null))), color: C.cyan, cov: cov.chat },
      { k: "ahtLC", label: "AHT Live Chat", v: kpis.ahtLC, fmt: (v) => `${fmtN(v, 0)}s`, pts: mk(s.chat.filter((r) => r.ch === "LC"), (rs) => mean(rs.map((r) => r.aht).filter((v) => v != null))), color: C.cyan, cov: cov.chat },
      { k: "frtWA", label: "FRT WhatsApp", v: kpis.frtWA, fmt: (v) => `${fmtN(v, 2)}m`, pts: mk(s.chat.filter((r) => r.ch === "WA"), (rs) => mean(rs.map((r) => r.frt).filter((v) => v != null))), color: C.blue, cov: cov.chat },
      { k: "frtLC", label: "FRT Live Chat", v: kpis.frtLC, fmt: (v) => `${fmtN(v, 2)}m`, pts: mk(s.chat.filter((r) => r.ch === "LC"), (rs) => mean(rs.map((r) => r.frt).filter((v) => v != null))), color: C.blue, cov: cov.chat },
    ];
  }, [slices, kpis, data, cov]);

  const sitePair = useMemo(() => {
    const q = kpisOf(filterData(data, { ...range, site: "Qatar" }));
    const i = kpisOf(filterData(data, { ...range, site: "India" }));
    return [
      { label: "VOC Top-2-Box", q: q.voc, i: i.voc, fmt: fmtPct },
      { label: "Surveys", q: q.surveys, i: i.surveys, fmt: fmtInt },
      { label: "PMT Score", q: q.pmt, i: i.pmt, fmt: fmtPct },
      { label: "QSL Score", q: q.qsl, i: i.qsl, fmt: fmtPct },
      { label: "AHT WhatsApp (s)", q: q.ahtWA, i: i.ahtWA, fmt: (v) => fmtN(v, 0) },
      { label: "AHT Live Chat (s)", q: q.ahtLC, i: i.ahtLC, fmt: (v) => fmtN(v, 0) },
      { label: "FRT WhatsApp (m)", q: q.frtWA, i: i.frtWA, fmt: (v) => fmtN(v, 2) },
      { label: "FRT Live Chat (m)", q: q.frtLC, i: i.frtLC, fmt: (v) => fmtN(v, 2) },
    ];
  }, [data, range]);

  const slaKpis = useMemo(() => data.sla.kpis.filter((k) => k.target != null), [data]);
  const segWeeks = useMemo(() => data.sla.weeks.filter((w) => w.segments[segment] && slaKpis.some((k) => w.segments[segment][k.name] != null)), [data, segment, slaKpis]);
  const lastWeeks = segWeeks.slice(-4);

  const trendCharts = useMemo(() => {
    const out = [];
    for (const name of SLA_TREND_PREF) {
      const kpi = data.sla.kpis.find((k) => k.name === name);
      if (!kpi) continue;
      const pts = data.sla.weeks.map((w) => ({ w: wkShort(w.label), v: w.segments[segment]?.[name] })).filter((p) => p.v != null);
      if (pts.length >= 2) out.push({ name, target: kpi.target, pts });
      if (out.length >= 10) break;
    }
    return out;
  }, [data, segment]);

  return (
    <div className="space-y-8">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {tiles.map((t) => (
          <KpiCard key={t.k} testid={`ic-kpi-${t.k}`} label={t.label} color={t.color} value={t.fmt(t.v)}
            badge={<TargetChip v={t.v} target={t.target} lower={t.lower} />} sub={`to ${covLabel(t.cov)}`}>
            <div className="mt-3"><Spark pts={t.pts} color={t.color} /></div>
          </KpiCard>
        ))}
      </div>

      <div className={card} data-testid="ic-site-split">
        <p className="font-mono-hud mb-1 text-[10px] uppercase tracking-[0.25em] text-thdim">Qatar vs India · selected range</p>
        <p className="mb-5 text-xs text-thdim">Ignores the Site filter by design</p>
        <div className="grid gap-x-10 gap-y-5 md:grid-cols-2">
          {sitePair.map((m) => {
            const max = Math.max(m.q || 0, m.i || 0) || 1;
            return (
              <div key={m.label}>
                <p className="mb-1.5 text-xs font-medium text-thbody">{m.label}</p>
                {[["Qatar", m.q, SITE_COLORS.Qatar], ["India", m.i, SITE_COLORS.India]].map(([s, v, col]) => (
                  <div key={s} className="mb-1 flex items-center gap-2">
                    <span className="w-11 font-mono-hud text-[9px] uppercase text-thdim">{s}</span>
                    <div className="h-3 flex-1 rounded-sm bg-thhover/10">
                      <div className="h-3 rounded-sm transition-all" style={{ width: `${v == null ? 0 : (100 * v) / max}%`, background: col }} />
                    </div>
                    <span className="w-14 text-right font-mono-hud text-[10px] text-thbody">{m.fmt(v)}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      <div className={card} data-testid="ic-sla-scorecard">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-mono-hud text-[10px] uppercase tracking-[0.25em] text-thdim">Weekly SLA Scorecard</p>
            <p className="mt-1 text-xs text-thdim">From the SLAs Tracker tab · not affected by the date filter</p>
          </div>
          <div className="flex gap-1 rounded-lg bg-thhover/5 p-1">
            {SEGMENTS.map((s) => (
              <button key={s} data-testid={`ic-sla-seg-${s.toLowerCase()}`} onClick={() => setSegment(s)}
                className={`rounded-md px-3 py-1 text-xs font-medium ${segment === s ? "bg-thcyan/15 text-thcyan" : "text-thmute hover:text-thbody"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        {lastWeeks.length === 0 ? (
          <p className="py-6 text-center text-sm text-thdim">No populated weeks for this segment yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead><tr className="border-b border-thborder/10">
                <th className="font-mono-hud px-3 py-2 text-[9px] uppercase tracking-[0.15em] text-thdim">KPI</th>
                <th className="font-mono-hud px-3 py-2 text-[9px] uppercase tracking-[0.15em] text-thdim">Target</th>
                {lastWeeks.map((w, i) => (
                  <th key={w.label} className={`font-mono-hud px-3 py-2 text-right text-[9px] uppercase tracking-[0.15em] ${i === lastWeeks.length - 1 ? "text-thcyan" : "text-thdim"}`}>{wkShort(w.label)}</th>
                ))}
                <th className="font-mono-hud px-3 py-2 text-[9px] uppercase tracking-[0.15em] text-thdim">Status</th>
              </tr></thead>
              <tbody>
                {slaKpis.map((k) => {
                  const latest = lastWeeks[lastWeeks.length - 1]?.segments[segment]?.[k.name];
                  const lower = k.name.toLowerCase().includes("shrinkage");
                  const met = latest == null ? null : lower ? latest <= k.target : latest >= k.target;
                  return (
                    <tr key={k.name} className="border-b border-thborder/5">
                      <td className="px-3 py-2 text-xs text-thbody">{k.name}</td>
                      <td className="px-3 py-2 font-mono-hud text-xs text-thdim">{k.pct ? fmtPct(k.target) : fmtN(k.target)}</td>
                      {lastWeeks.map((w, i) => {
                        const v = w.segments[segment]?.[k.name];
                        return <td key={w.label} className={`px-3 py-2 text-right font-mono-hud text-xs ${i === lastWeeks.length - 1 ? "font-semibold text-thstrong" : "text-thbody"}`}>{v == null ? "—" : k.pct ? fmtPct(v) : fmtN(v)}</td>;
                      })}
                      <td className="px-3 py-2">
                        {met == null ? <span className="text-xs text-thdim">—</span> : (
                          <span className={`rounded-full px-2 py-0.5 font-mono-hud text-[9px] uppercase ${met ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#F43F5E]/15 text-[#F43F5E]"}`}>{met ? "met" : "miss"}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div data-testid="ic-sla-trends">
        <p className="font-mono-hud mb-4 text-[10px] uppercase tracking-[0.25em] text-thdim">Weekly SLA Trends · {segment} · full history</p>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {trendCharts.map((c) => (
            <div key={c.name} className={card}>
              <p className="mb-2 truncate text-xs font-medium text-thbody">{c.name}</p>
              <div className="h-36">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={100} initialDimension={{ width: 1, height: 1 }}>
                  <LineChart data={c.pts} margin={{ top: 4, right: 8, bottom: 0, left: -18 }}>
                    <XAxis dataKey="w" tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} interval="preserveStartEnd" />
                    <YAxis tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} domain={["auto", "auto"]} />
                    <Tooltip contentStyle={{ background: "rgb(var(--th-solid))", border: "1px solid rgb(var(--th-border) / 0.14)", borderRadius: 10, fontSize: 11, color: "rgb(var(--th-strong))" }} labelStyle={{ color: "rgb(var(--th-mute))" }} />
                    {c.target != null && <ReferenceLine y={c.target} stroke={C.yellow} strokeDasharray="5 4" strokeOpacity={0.7} />}
                    <Line type="monotone" dataKey="v" stroke={C.cyan} strokeWidth={2} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
